import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { QRCodeSVG } from "qrcode.react";
import { Link2, Users, LayoutDashboard, PlayCircle, Copy, Mail, FileBadge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { BrandLogo } from "@/components/BrandLogo";

const LINKS = [
  { to: "/me", label: "Dashboard", icon: LayoutDashboard, testId: "nav-me-dashboard" },
  { to: "/me/students", label: "My Students", icon: Users, testId: "nav-me-students" },
  { to: "/me/link", label: "My Link", icon: Link2, testId: "nav-me-link" },
  { to: "/me/inbox", label: "Inbox", icon: Mail, testId: "nav-me-inbox" },
  { to: "/me/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-me-sample-report" },
  { to: "/me/demo", label: "Launch Demo", icon: PlayCircle, testId: "nav-me-demo" },
];

export default function MarketingLink() {
  const [exec, setExec] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignName, setCampaignName] = useState("");
  const [campaignUtm, setCampaignUtm] = useState("");

  useEffect(() => {
    api.get("/marketing/me").then((res) => setExec(res.data));
    api.get("/marketing/campaigns").then((res) => setCampaigns(res.data));
  }, []);

  const link = exec ? `${window.location.origin}/start?ref=${exec.referral_code}` : "";

  const copy = () => {
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  };

  const createCampaign = async () => {
    if (!campaignName || !campaignUtm) return;
    const res = await api.post("/marketing/campaigns", { name: campaignName, utm_campaign: campaignUtm });
    setCampaigns((c) => [...c, res.data]);
    setCampaignName(""); setCampaignUtm("");
  };

  return (
    <DashboardShell links={LINKS} activePath="/me/link">
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2" data-testid="marketing-link-heading">My CareerStudy360 Link</h1>
      <p className="text-sm text-slate-500 mb-8">
        Students who register using this link will automatically appear in your CareerStudy360 dashboard.
      </p>

      <div className="rounded-xl border border-slate-200 bg-white p-8 grid sm:grid-cols-[1fr_auto] gap-8 items-center mb-10">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Your CareerStudy360 Referral Link</p>
          <div className="flex gap-2">
            <Input readOnly value={link} data-testid="referral-link-input" className="font-mono text-sm" />
            <Button data-testid="referral-link-copy-button" onClick={copy} variant="outline"><Copy className="h-4 w-4" /></Button>
          </div>
          <div className="flex gap-3 mt-4">
            <a data-testid="referral-share-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(link)}`} target="_blank" rel="noreferrer" className="text-sm text-emerald-600 hover:underline">Share on WhatsApp</a>
            <a data-testid="referral-share-email" href={`mailto:?subject=CareerStudy360&body=${encodeURIComponent(link)}`} className="text-sm text-indigo-600 hover:underline">Share via Email</a>
            <a data-testid="referral-share-sms" href={`sms:?body=${encodeURIComponent(link)}`} className="text-sm text-slate-600 hover:underline">Share via SMS</a>
          </div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-slate-100 flex flex-col items-center gap-3" data-testid="referral-qr-code">
          <BrandLogo className="h-6" />
          {link && <QRCodeSVG value={link} size={140} />}
          <div className="text-center">
            <p className="text-xs font-semibold text-slate-700 leading-tight" data-testid="referral-qr-attribution-name">
              {exec?.company_name || exec?.name || ""}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">Scan to take your CareerStudy360 Assessment</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="font-heading font-semibold text-slate-900 mb-4">Campaign Links</h3>
        <div className="flex gap-3 mb-6 flex-wrap">
          <Input placeholder="Campaign name (e.g. School Presentation)" value={campaignName} data-testid="campaign-name-input" onChange={(e) => setCampaignName(e.target.value)} className="max-w-xs" />
          <Input placeholder="utm_campaign (e.g. st_joseph_school)" value={campaignUtm} data-testid="campaign-utm-input" onChange={(e) => setCampaignUtm(e.target.value)} className="max-w-xs" />
          <Button data-testid="campaign-create-button" onClick={createCampaign} className="bg-indigo-600 hover:bg-indigo-700 rounded-full">Create</Button>
        </div>
        <div className="space-y-2">
          {campaigns.map((c) => (
            <div key={c.campaign_id} className="text-sm flex justify-between border-b border-slate-100 py-2" data-testid={`campaign-row-${c.campaign_id}`}>
              <span className="font-medium text-slate-700">{c.name}</span>
              <span className="font-mono text-slate-500">{window.location.origin}/start?ref={c.referral_code}&utm_campaign={c.utm_campaign}</span>
            </div>
          ))}
        </div>
      </div>
    </DashboardShell>
  );
}
